import { Effect, Option, Schema, SchemaGetter, SchemaIssue } from 'effect';

import type { StripJsonLd } from '../transform/withoutJsonLd';
import { extractPageFromUrl } from '../url/extractPageFromUrl';

export const HydraView = Schema.Struct({
  '@id': Schema.String,
  '@type': Schema.String,
  'hydra:first': Schema.optional(Schema.String),
  'hydra:last': Schema.optional(Schema.String),
  'hydra:previous': Schema.optional(Schema.String),
  'hydra:next': Schema.optional(Schema.String),
}).annotate({ identifier: 'HydraView' });

export type HydraView = Schema.Schema.Type<typeof HydraView>;

export const HydraViewWithNumbers = Schema.Struct({
  current: Schema.Finite,
  first: Schema.Finite,
  last: Schema.Finite,
  previous: Schema.Finite,
  next: Schema.Finite,
  hasPrev: Schema.Boolean,
  hasNext: Schema.Boolean,
  isFirst: Schema.Boolean,
  isLast: Schema.Boolean,
});

export type HydraViewWithNumbers = Schema.Schema.Type<typeof HydraViewWithNumbers>;

export const HydraViewExtended = Schema.Struct({
  hydraView: HydraView,
  hydraViewNumbers: HydraViewWithNumbers,
});

export type HydraViewExtended = StripJsonLd<Schema.Schema.Type<typeof HydraViewExtended>>;

export const HydraViewToHydraViewWithNumbers = HydraView.pipe(
  Schema.decodeTo(HydraViewWithNumbers, {
    decode: SchemaGetter.transformEffect((hydraPagination) =>
      Effect.gen(function* () {
        const current = yield* extractPageFromUrl(hydraPagination['@id']).pipe(
          Effect.orElseSucceed(() => 1)
        );

        const first = hydraPagination['hydra:first']
          ? yield* extractPageFromUrl(hydraPagination['hydra:first']).pipe(
              Effect.orElseSucceed(() => 1)
            )
          : 1;
        const last = hydraPagination['hydra:last']
          ? yield* extractPageFromUrl(hydraPagination['hydra:last']).pipe(
              Effect.orElseSucceed(() => 1)
            )
          : 1;
        const previous = hydraPagination['hydra:previous']
          ? yield* extractPageFromUrl(hydraPagination['hydra:previous']).pipe(
              Effect.orElseSucceed(() => 1)
            )
          : current;
        const next = hydraPagination['hydra:next']
          ? yield* extractPageFromUrl(hydraPagination['hydra:next']).pipe(
              Effect.orElseSucceed(() => current)
            )
          : current;

        // Compute boolean flags
        const isFirst = current === first;
        const isLast = current === last;
        const hasPrev = current > first;
        const hasNext = current < last;

        return {
          current,
          first,
          last,
          previous,
          next,
          hasPrev,
          hasNext,
          isFirst,
          isLast,
        };
      })
    ),
    encode: SchemaGetter.forbidden(() => 'Encoding HydraPagination from page numbers is forbidden'),
  })
).annotate({ identifier: 'HydraPaginationToNumbers' });

export const HydraViewToHydraViewExtended = HydraView.pipe(
  Schema.decodeTo(HydraViewExtended, {
    decode: SchemaGetter.transformEffect((hydraView) =>
      Effect.gen(function* () {
        const hydraViewNumbers = yield* Schema.decodeEffect(HydraViewToHydraViewWithNumbers)(
          hydraView
        ).pipe(
          Effect.mapError((error) => new SchemaIssue.InvalidValue({ message: error.message }))
        );

        return {
          hydraView,
          hydraViewNumbers,
        };
      })
    ),
    encode: SchemaGetter.forbidden(() => 'Encoding HydraViewExtended to HydraView is forbidden'),
  })
).annotate({ identifier: 'HydraViewToHydraViewExtended' });
